import { Division, EntityLabel, Site, Subdivision } from '@esg/esg-global-types';
import { auth } from '../google/firebase';
import { apollo_client } from '../app/db_util';
import {
  ALLOW_DELETE_SUBDIVISIONS_QUERY,
  GET_SUBDIVISIONS_QUERY
} from '../graphql/queries/subdivision';
import {
  ARCHIVE_SUBDIVISION_MUTATOR,
  CREATE_SUBDIVISION_MUTATOR,
  DELETE_SUBDIVISION_MUTATOR,
  UNARCHIVE_SUBDIVISION_MUTATOR,
  UPDATE_SUBDIVISION_MUTATOR
} from '../graphql/mutations/subdivisions';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { DocumentReference } from 'firebase/firestore';

// Singular and plural label for model entity.
export const subdivision_label: EntityLabel = {
  one: 'Subdivision',
  many: 'Subdivisions'
};

/**
 * Query all subdivision documents for current portal company.
 * @param {string} group_id Group ID of current portal company.
 * @param {string} company_id ID of current portal company to reference firestore documents
 * @returns {Promise<Array<Subdivision>>}
 */
export const getSubdivisions = async (
  group_id: string,
  company_id: string
): Promise<Array<Subdivision>> => {
  try {
    const subdivisions: Array<Subdivision> = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id
        },
        query: GET_SUBDIVISIONS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.subdivisions;
      });
    return subdivisions;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/subdivision.ts failed on an unknown error while calling getSubdivisions.',
      {
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Create subdivision with relative data.
 * @param {string} group_id ID of Group which Subdivision is being created for.
 * @param {string} company_id ID of Company which Subdivision is being created for.
 * @param {string} subdivision_name Name of new Subdivision.
 * @param {Division} division Parent Division of Subdivision.
 * @returns {Promise<DocumentReference>}
 */
export const createSubdivision = async (
  group_id: string,
  company_id: string,
  subdivision_name: string,
  division: Division
): Promise<DocumentReference> => {
  try {
    const new_subdivision = await apollo_client
      .mutate({
        mutation: CREATE_SUBDIVISION_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          subdivision_name: subdivision_name,
          division: division,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.createSubdivision;
      });
    return new_subdivision;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/subdivision.ts failed on an unknown error while calling createSubdivision.',
      {
        group_id: group_id,
        company_id: company_id,
        subdivision_name: subdivision_name,
        division: division
      },
      tracking_id
    );
  }
};

/**
 * Update subdivision with relative data.
 * @param {string} group_id ID of Group to update subdivision for.
 * @param {string} company_id ID of Company to update subdivision for.
 * @param {Subdivision} updated_subdivision New Subdivision data to push into document.
 * @param {Subdivision} original_data Old subdivision data for audit log tracking.
 * @returns {Promise<void>}
 */
export const updateSubdivision = async (
  group_id: string,
  company_id: string,
  updated_subdivision: Subdivision,
  original_data: Subdivision
) => {
  try {
    if (group_id && company_id) {
      await apollo_client
        .mutate({
          mutation: UPDATE_SUBDIVISION_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            updated_subdivision: updated_subdivision,
            original_data: original_data,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.updateSubdivision;
        });
    }
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/subdivision.ts failed on an unknown error while calling updateSubdivision.',
      {
        group_id: group_id,
        company_id: company_id,
        updated_subdivision: updated_subdivision,
        original_data: original_data
      },
      tracking_id
    );
  }
};

/**
 * Delete subdivision.
 * @param {string} group_id ID of Group to delete subdivision for.
 * @param {string} company_id ID of Company to delete subdivision for.
 * @param {string} subdivision_id ID of subdivision to delete.
 * @param {Array<Site>} delete_sites Sites to cascade delete with subdivision.
 * @returns {Promise<void>}
 */
export const deleteSubdivision = async (
  group_id: string,
  company_id: string,
  subdivision_id: string,
  subdivision_name: string,
  delete_sites?: Array<Site>
): Promise<void> => {
  try {
    if (group_id && company_id && subdivision_id) {
      await apollo_client
        .mutate({
          mutation: DELETE_SUBDIVISION_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            subdivision_id: subdivision_id,
            subdivision_name: subdivision_name,
            delete_sites: delete_sites,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.deleteSubdivision;
        });
    }
    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/subdivision.ts failed on an unknown error while calling deleteSubdivision.',
      {
        group_id: group_id,
        company_id: company_id,
        subdivision_id: subdivision_id,
        subdivision_name: subdivision_name,
        delete_sites: delete_sites
      },
      tracking_id
    );
  }
};

/**
 * Archive subdivision.
 * @param {string} group_id ID of Group to archive subdivision for.
 * @param {string} company_id ID of Company to archive subdivision for.
 * @param {string} subdivision_id ID of subdivision to archive.
 * @param {Array<Site>} archive_sites Sites to cascade archive with subdivision.
 * @returns {Promise<void>}
 */
export const archiveSubdivision = async (
  group_id: string,
  company_id: string,
  subdivision_id: string,
  archive_sites?: Array<Site>
): Promise<void> => {
  try {
    if (group_id && company_id && subdivision_id) {
      await apollo_client
        .mutate({
          mutation: ARCHIVE_SUBDIVISION_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            subdivision_id: subdivision_id,
            archive_sites: archive_sites,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.archiveSubdivision;
        });
    }
    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/subdivision.ts failed on an unknown error while calling archiveSubdivision.',
      {
        group_id: group_id,
        company_id: company_id,
        subdivision_id: subdivision_id,
        archive_sites: archive_sites
      },
      tracking_id
    );
  }
};

/**
 * Unarchive subdivision.
 * @param {string} group_id ID of Group to unarchive subdivision for.
 * @param {string} company_id ID of Company to unarchive subdivision for.
 * @param {string} subdivision_id ID of subdivision to unarchive.
 * @param {Array<Site> | undefined} unarchive_sites Sites to cascade unarchive with subdivision.
 * @returns {Promise<void>}
 */
export const unarchiveSubdivision = async (
  group_id: string,
  company_id: string,
  subdivision_id: string,
  unarchive_sites?: Array<Site>
): Promise<void> => {
  try {
    if (group_id && company_id && subdivision_id) {
      await apollo_client
        .mutate({
          mutation: UNARCHIVE_SUBDIVISION_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            subdivision_id: subdivision_id,
            unarchive_sites: unarchive_sites,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.unarchiveSubdivision;
        });
    }
    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/subdivision.ts failed on an unknown error while calling unarchiveSubdivision.',
      {
        group_id: group_id,
        company_id: company_id,
        subdivision_id: subdivision_id,
        unarchive_sites: unarchive_sites
      },
      tracking_id
    );
  }
};

/**
 * Check if a Subdivision can be safely deleted
 * @param {string} group_id ID of Group to check Subdivision for
 * @param {string} company_id ID of Company to check Subdivision for
 * @param {string} subdivision_id ID of subdivision to check
 * @returns {Promise<boolean>}
 */
export const allowDeleteSubdivision = async (
  group_id: string,
  company_id: string,
  subdivision_id: string
): Promise<boolean> => {
  try {
    const allow_delete: boolean = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id,
          subdivision_id: subdivision_id
        },
        query: ALLOW_DELETE_SUBDIVISIONS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.allowDeleteSubdivision;
      });
    return allow_delete;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/subdivision.ts failed on an unknown error while calling allowDeleteSubdivision.',
      {
        group_id: group_id,
        company_id: company_id,
        subdivision_id: subdivision_id
      },
      tracking_id
    );
  }
};
